import type { HttpError, HttpErrorRecord } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

import type { UseUploadMutationVariables } from "../services.types"

type UseUploadUsersOptions = Omit<
  UseMutationOptions<
    unknown,
    HttpError<HttpErrorRecord>,
    UseUploadMutationVariables
  >,
  "mutationFn"
>

const uploadUsers = async ({
  file,
  organizationId,
}: UseUploadMutationVariables) => {
  try {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("organization_id", organizationId ?? "")

    const response = await httpClient.post(
      "/api/v1/data_manager/user_uploads",
      formData
    )

    return response
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Uploads a CSV file of user data (bulk user creation)
 *
 * @example
 * const {
 *   error,
 *   isError,
 *   isPending,
 *   mutate
 * } = useUploadUsers(options)
 *
 * mutate({ file, organizationId })
 */
export const useUploadUsers = (options: UseUploadUsersOptions = {}) => {
  const mutationOptions = options

  const mutation = useMutation({
    ...mutationOptions,
    meta: { ...mutationOptions.meta, handlesOwnErrors: true },
    mutationFn: uploadUsers,
  })

  return mutation
}
